import React, {
	Suspense,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { history } from "./helpers/history";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Route, Router, Switch } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// import * as Sentry from "@sentry/react";
import { lazy } from "react";
import AppProvider, { AppContext } from "./AppContext";
import LoadingScreen from "./components/loader/LoadingScreen";
import MobileBottomNav from "./components/navBottom";
import { RootState } from "./store";
import { isNullOrUndefined } from "./utils";
import { ResponseData } from "./model/Responses";
import { checkToken } from "./services/player.service";
import { logoutAction } from "./actions/auth";
import useClientWebsite from "./components/getClientWebsite";
import config from "./config";
import Footer from "./components/footer";
import { modeTypes } from "./constants/mode";
import BackButton from "./components/backButton/BackButton";
import LogoutConfirmationModal from "./components/LogoutConfirmationModal";
import DepositConfirm from "./views/DepositConfirm";

const Dashboard = lazy(() => import("./views/Dashboard"));
const Lobby = lazy(() => import("./views/Lobby"));
const Transfer = lazy(() => import("./views/Transfer"));
const Download = lazy(() => import("./views/Download"));
// const SupportModal = lazy(() => import("./components/SupportModal"));
const RedirectLogin = lazy(() => import("./views/RedirectLogin"));
const Promotion = lazy(() => import("./views/Promotion"));
const Chat = lazy(() => import("./views/Chat"));
const Deposit = lazy(() => import("./views/Deposit"));
const Bonus = lazy(() => import("./views/Bonus"));
const Withdraw = lazy(() => import("./views/Withdraw"));
const Send = lazy(() => import("./views/Send"));
const Rebate = lazy(() => import("./views/Rebate"));
const Claims = lazy(() => import("./views/Claims"));
const BankAccount = lazy(() => import("./views/BankAccount"));
const Security = lazy(() => import("./views/Security"));
const ReportProblem = lazy(() => import("./views/ReportProblem"));
const SuccessPayment = lazy(() => import("./views/SuccessPayment"));
const FailPayment = lazy(() => import("./views/FailPayment"));

const STANDALONE_ROUTES = ["/success-payment", "/fail-payment"];
// DepositConfirm is imported eagerly so /deposit/confirm does not load a separate lazy chunk
// (with homepage "./", that chunk URL was resolved under /deposit/static/... on stage).

// const { userProfile } = useSelector((state: any) => state.userProfile);

// Sentry.init({
// 	dsn: "https://4e748ab9e202ee6dc77f9e4dd018c28e@o4508758524428288.ingest.de.sentry.io/4509410193899600",
// 	integrations: [new Sentry.BrowserTracing()],
// 	tracesSampleRate: 1.0,
// });

const App: React.FC = () => {
	const token = sessionStorage.getItem("user_token");
	const { i18n } = useTranslation();
	const { loading, language } = useSelector((state: RootState) => state.store);
	const { userProfile } = useSelector((state: any) => state.userProfile);
	const dispatch = useDispatch();
	const [themeLoading, setThemeLoading] = useState<boolean>(true);
	const [clientName] = useState("");
	const currentMode = localStorage.getItem("currentMode") || modeTypes.TRANSFER;
	const [path, setPath] = useState("");
	const [pathname, setPathname] = useState(history.location.pathname);
	const isStandalonePage = STANDALONE_ROUTES.includes(pathname);

	//here will be replaced by using detection on the device user use
	//to return the specific theme selected by user for the specific device

	const Sidebar = lazy(() =>
		import(`./components/client/${config.client}/common/sidebar`).catch(() => {
			return import("./components/menu/sidebar");
		})
	);
	const Header = lazy(() =>
		import(`./components/client/${config.client}/common/header`).catch(() => {
			return import("./components/header");
		})
	);

	const routes = [
		{
			path: "/",
			exact: true,
			name: "Home",
			component:
				!isNullOrUndefined(userProfile) && currentMode == modeTypes.TRANSFER
					? Lobby
					: Dashboard,
			access: true,
		},
		{
			path: "/promotion",
			name: "Promotion",
			component: Promotion,
			access: true,
		},
		{ path: "/home", name: "Home", component: Dashboard, access: true },
		{
			path: "/lobby",
			name: "Lobby",
			component: Lobby,
			access: !isNullOrUndefined(userProfile),
		},
		{
			path: "/transfer",
			name: "Transfer",
			component: Transfer,
			access: !isNullOrUndefined(userProfile),
		},
		{
			path: "/chat",
			name: "Chat",
			component: Chat,
			access: true,
		},
		{
			path: "/deposit/confirm",
			name: "DepositConfirm",
			component: DepositConfirm,
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
		},
		{
			path: "/bank-account",
			name: "BankAccount",
			component: BankAccount,
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
		},
		{
			path: "/security",
			name: "Security",
			component: Security,
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
		},
		{
			path: "/deposit",
			name: "Deposit",
			component: Deposit,
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
		},
		{
			path: "/bonus",
			name: "Bonus",
			component: Bonus,
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
		},
		{
			path: "/withdraw",
			name: "Withdraw",
			component: Withdraw,
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
		},
		{
			path: "/send",
			name: "Send",
			component: Send,
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
		},
		{
			path: "/rebate",
			name: "Rebate",
			component: Rebate,
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
		},
		{
			path: "/claims",
			name: "Claims",
			component: Claims,
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
		},
		{
			path: "/report-problem",
			name: "ReportProblem",
			component: ReportProblem,
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
		},
		{ path: "/download", name: "Download", component: Download, access: true },
		{
			path: "/success-payment",
			name: "SuccessPayment",
			component: SuccessPayment,
			access: true,
		},
		{
			path: "/fail-payment",
			name: "FailPayment",
			component: FailPayment,
			access: true,
		},
		{
			path: "/login",
			exact: false,
			name: "RedirectLogin",
			component: RedirectLogin,
			access: true,
		},
	];

	useClientWebsite(clientName, setThemeLoading);

	useEffect(() => {
		localStorage.setItem("rememberMe", "true");
	}, []);

	useEffect(() => {
		const unlisten = history.listen((location) => {
			setPathname(location.pathname);
		});
		setPathname(history.location.pathname);
		return unlisten;
	}, []);

	useEffect(() => {
		if (i18n.language !== language) i18n.changeLanguage(language);
	}, [language]);

	useEffect(() => {
		if (!token) return;
		const interval = setInterval(async () => {
			try {
				const result: ResponseData<any> = await checkToken(
					token,
					localStorage.getItem("currentMode") || modeTypes.TRANSFER
				);
				if (!result.status) {
					toast.error(result.message);
					dispatch(logoutAction());
					return;
				}
			} catch (error) {
				console.error("Token validation error:", error);
				dispatch(logoutAction());
			}
		}, 300000); // Poll every 5 minutes

		return () => clearInterval(interval);
	}, [token]);

	const menu = routes.map((route, index) => {
		return route.component && route.access === true ? (
			<Route
				key={index}
				path={route.path}
				exact={route.exact}
				render={(props: any) => {
					setPath(route.name);
					return <route.component {...props} />;
				}}
				// render={(props: any) => <route.component {...props} />}
			/>
		) : null;
	});

	return (
		<div className={`wrapper${isStandalonePage ? " wrapper--standalone" : ""}`}>
			<Router history={history}>
				<AppProvider>
					<DeepLinkHandler />
					<Suspense fallback={<LoadingScreen loading={loading} />}>
						{loading && <LoadingScreen loading={loading} />}
						{themeLoading && <LoadingScreen loading={themeLoading} />}
						{!isStandalonePage && <Sidebar />}
						{/* <SupportModal /> */}
						{!themeLoading &&
							(isStandalonePage ? (
								<Switch>{menu}</Switch>
							) : (
								<div
									className={`sidebar-right ${currentMode === modeTypes.SEAMLESS ? "seamless-mode" : ""}`}
								>
									<Header />
									{/* {!isNullOrUndefined(userProfile) &&
									currentMode == modeTypes.SEAMLESS && path == 'Lobby' && <BackButton />} */}
									<Switch>{menu}</Switch>
								</div>
							))}
					</Suspense>
					<ToastContainer autoClose={3000} theme="dark" />
					{!isStandalonePage && <MobileBottomNav />}
					<LogoutConfirmationModal />
					{/* <Footer /> */}
				</AppProvider>
			</Router>
		</div>
	);
};

export default App;

// Top-level stable component that handles deep links like ?action=register&ref=CODE
// It runs once on mount, opens the login modal (which will show the register form),
// stores the referral code in context, and cleans the URL.
const DeepLinkHandler: React.FC = () => {
	const { setModalState, setReferralCodeFromLink } = useContext(AppContext)!;

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const action = params.get("action");

		if (action === "register") {
			const ref = params.get("ref") || "";
			if (ref) {
				setReferralCodeFromLink(ref);
			}
			// Open the login modal. The LoginComponent will react to the referralCodeFromLink
			// and switch its internal view to the register form.
			setModalState("login");
			// Clean the URL so the user doesn't see the query params.
			history.replace("/");
		}
	}, [setModalState, setReferralCodeFromLink]);

	return null;
};
